import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAccessToken, useAuth } from '@dss/auth'
import { ApiError, acceptGroupInvitation, previewGroupInvitation, registerGroupInvitation,
  acceptEmployeeInvitation, previewEmployeeInvitation, registerEmployeeInvitation,
  acceptOwnerInvitation, previewOwnerInvitation, registerOwnerInvitation,
  type OwnerInvitationPreview, type EmployeeInvitationPreview, type GroupInvitationPreview, type GroupMembership } from '@dss/api-client'
import { Banner, Button, Card, Input } from '@dss/design-system'
import { api } from '../../lib/api'
import { Link, useLocation } from '../../lib/router'
import { distributorWorkspacePath } from '../../lib/distributorWorkspace'
import { forgetInvitation, rememberInvitation, validInvitationSecret } from './invitationReturn'
import './invitations.css'

export function GroupInvitationScreen() {
  return <InvitationScreen kind="group" />
}
export function EmployeeInvitationScreen() {
  return <InvitationScreen kind="employee" />
}
export function OwnerInvitationScreen() {
  return <InvitationScreen kind="owner" />
}
type InvitationKind = 'group' | 'employee' | 'owner'
function InvitationScreen({ kind }: { kind: InvitationKind }) {
  const { hash } = useLocation()
  const auth = useAuth()
  const secret = hash.slice(1)
  return <main className="group-invitation-page">
    {validInvitationSecret(secret) ? <InvitationAcceptance key={`${kind}:${secret}:${auth.user?.profile.sub ?? 'anonymous'}`} secret={secret} kind={kind} />
      : <Card padding="lg"><h1>Нужна полная ссылка приглашения</h1>
        <p>Откройте ссылку целиком из сообщения владельца или менеджера. Не публикуйте её.</p>
        <Link to="/me">Перейти в портал</Link></Card>}
  </main>
}

function InvitationAcceptance({ secret, kind }: { secret: string; kind: InvitationKind }) {
  const auth = useAuth()
  const token = useAccessToken()
  const queryClient = useQueryClient()
  const [preview, setPreview] = useState<GroupInvitationPreview | EmployeeInvitationPreview | OwnerInvitationPreview | null>(null)
  const [failure, setFailure] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const [pending, setPending] = useState(false)
  const [password, setPassword] = useState('')
  const [consent, setConsent] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [receipt, setReceipt] = useState<GroupMembership | null>(null)
  const [reload, setReload] = useState(0)
  const alive = useRef(true)
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])
  const handleFailure = useCallback((error: unknown) => {
    if (!alive.current) return
    if (error instanceof ApiError && error.status === 410) {
      setPreview(null); setPassword(''); setConsent(false); setConfirmed(false); setStatus(null)
      forgetInvitation()
    }
    setFailure(error)
  }, [])
  useEffect(() => {
    const controller = new AbortController()
    setLoading(true); setFailure(null)
    const read = kind === 'group' ? previewGroupInvitation : kind === 'owner' ? previewOwnerInvitation : previewEmployeeInvitation
    void read(api, secret, controller.signal).then((result) => {
      if (!controller.signal.aborted) setPreview(result)
    }).catch((error: unknown) => {
      if (!controller.signal.aborted) handleFailure(error)
    }).finally(() => { if (!controller.signal.aborted) setLoading(false) })
    return () => controller.abort()
  }, [secret, reload, kind, handleFailure])

  async function register(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending || !consent) return
    setPending(true); setFailure(null)
    try {
      const registerAccount = kind === 'group' ? registerGroupInvitation : kind === 'owner' ? registerOwnerInvitation : registerEmployeeInvitation
      const result = await registerAccount(api, secret, password, consent)
      if (alive.current) setStatus(result.status)
    } catch (error) { handleFailure(error) } finally { if (alive.current) { setPending(false); setPassword('') } }
  }
  async function login() {
    if (!preview || pending) return
    setPending(true); setFailure(null)
    try {
      rememberInvitation(secret, preview.invitation.expiresAt, kind)
      await auth.login()
    } catch {
      setFailure(new Error('Не удалось начать защищённый вход'))
      setPending(false)
    }
  }
  async function accept() {
    if (!token || !confirmed || pending) return
    setPending(true); setFailure(null)
    try {
      const result = kind === 'owner' ? await acceptOwnerInvitation(api, token, secret, confirmed)
        : await (kind === 'group' ? acceptGroupInvitation : acceptEmployeeInvitation)(api, token, secret)
      if (!alive.current) return
      setReceipt(result); forgetInvitation()
      // Подтверждённый сервером результат не зависит от обновления соседних кэшей.
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ['portal-session'] }),
        queryClient.invalidateQueries({ queryKey: ['portal-access'] }),
      ])
    } catch (error) { handleFailure(error) } finally { if (alive.current) setPending(false) }
  }
  if (loading) return <p role="status">Проверяем приглашение…</p>
  if (!receipt && failure instanceof ApiError && failure.status === 410) {
    return <Card padding="lg"><h1>Приглашение больше не действует</h1>
      <p role="alert">Приглашение отозвано или срок истёк. Попросите отправителя выдать новую ссылку.</p>
      <p>Подтвердить доступ по этой ссылке нельзя. Данные получателя и прежние права скрыты.</p>
      <Link to="/me">Перейти в портал</Link>
    </Card>
  }
  if (kind === 'owner' && failure instanceof ApiError && [401, 403].includes(failure.status)) {
    return <Card padding="lg"><h1>Доступ к приглашению не подтверждён</h1>
      <p role="alert">Данные получателя и права скрыты. Аккаунт должен соответствовать адресату, а приглашающий владелец — сохранять полномочия.</p>
      <Button disabled={pending} onClick={() => { setPreview(null); setConfirmed(false); setReload(value => value + 1) }}>Проверить приглашение снова</Button>
      {auth.isAuthenticated && <Button onClick={() => void auth.logout().catch(() => setFailure(new Error('Ошибка выхода')))}>Выйти из аккаунта</Button>}
    </Card>
  }
  if (!preview) return <Card padding="lg"><h1>Приглашение недоступно</h1>
    <p role="alert">Ссылка не найдена, доступ изменён или сервер временно недоступен.</p>
    <Button onClick={() => setReload(reload + 1)}>Проверить ещё раз</Button>
    <p>Если ошибка повторяется, попросите отправителя проверить приглашение.</p></Card>
  const invite = preview.invitation
  const ended = ['REVOKED', 'EXPIRED'].includes(invite.state)
    || Date.parse(invite.expiresAt) <= Date.now() && invite.state !== 'ACCEPTED'
  const adminTarget = distributorWorkspacePath(receipt?.role)
  return <Card padding="lg">
    <h1>{kind === 'owner' ? 'Приглашение равноправного владельца' : kind === 'employee' ? 'Приглашение сотрудника дистрибьютора' : 'Приглашение в клиентскую группу'}</h1>
    <p><strong>{preview.distributorName ?? preview.distributorSubdomain}</strong>
      {'groupName' in preview && <> · группа «{preview.groupName}»</>}</p>
    <p>Адресат: {invite.name} · {invite.email}</p>
    <p>Роль: {invite.role === 'DISTRIBUTOR_OWNER' ? 'Владелец дистрибьютора' : invite.role === 'DISTRIBUTOR_EMPLOYEE' ? 'Менеджер дистрибьютора'
      : invite.role === 'CLIENT_OWNER' ? 'Владелец клиента' : 'Сотрудник клиента'}</p>
    {'sourceManagerId' in invite ? <section>
      <h2>Связь с сотрудником 1С</h2>
      <p>{invite.sourceManagerName} · №{invite.sourceManagerId}</p>
      <p>UUID 1С: {invite.sourceManagerExternalId}</p>
      <p>Рабочая область определяется текущим назначением клиентов этому менеджеру в 1С.
        Это не права владельца дистрибьютора. Изменение назначения в 1С меняет вашу область доступа.</p>
      <p>Разрешены просмотр заказов и согласование плательщиков торговых точек в вашей области,
        просмотр и изменение собственного профиля. Приглашение сотрудников и управление ими не разрешены.</p>
    </section> : 'canCreateOrders' in invite ? <p>{invite.canCreateOrders ? 'Разрешены корзина и создание заказов.'
      : 'Только просмотр: корзина и создание заказов не разрешены.'}</p> : <Banner variant="warning"
        body="Вы получите такие же права, как пригласивший вас владелец, и доступ ко всем данным этого дистрибьютора. Вы станете равноправным владельцем и сможете приглашать других владельцев. Отключение или понижение владельца другим владельцем в первой версии недоступно." />}
    {'groupName' in preview && (preview.invitation.equalGroupOwner ? <Banner variant="info" body="Вы получите такие же права и увидите те же разрешённые данные всей группы, что и другие владельцы клиента. Вы сможете делать заказы и приглашать сотрудников. Это не доступ к управлению дистрибьютором." />
      : <><h2>Ваши торговые точки и плательщики</h2><ul className="invitation-pairs">
        {preview.pairLabels.map((pair) => <li key={`${pair.tradepointId}:${pair.payerId}`}>
          <strong>{pair.tradepointName ?? 'Точка'} · №{pair.tradepointId}</strong>
          <div>{pair.address ?? 'Адрес не указан'}</div>
          <div>{pair.payerName ?? 'Плательщик'} · №{pair.payerId} · ИНН {pair.inn ?? 'не указан'}</div>
        </li>)}</ul><p>Другие и будущие точки автоматически не назначаются.</p></>)}
    {receipt ? <div role="status"><h2>{receipt.state === 'ACTIVE' ? 'Доступ подтверждён' : 'Приглашение уже было принято'}</h2>
      {receipt.state === 'ACTIVE' ? adminTarget
        ? <a href={adminTarget} className="btn btn--primary" onClick={() => forgetInvitation()}>Открыть рабочее место дистрибьютора</a>
        : <Link to="/me" className="btn btn--primary"
          onClick={() => forgetInvitation()}>Открыть портал</Link>
        : <p>Текущий доступ заблокирован. Повтор ссылки его не восстановит; обратитесь к владельцу.</p>}</div>
      : ended ? <Banner variant="warning" body="Приглашение отозвано или срок истёк. Попросите отправителя выдать новую ссылку." />
        : <>
          <p>Срок действия: {new Date(invite.expiresAt).toLocaleString('ru-RU')}.</p>
          {Boolean(failure) && <Banner variant="danger" body={failure instanceof ApiError
            && failure.status === 400
            ? `Сервер отклонил данные: ${failure.message.slice(0, 700)}. Исправьте введённые данные; действие не выполнено.`
            : failure instanceof ApiError && [401, 403].includes(failure.status)
            ? 'Нужен подтверждённый аккаунт адресата, действующий в этом дистрибьюторе. Полномочия отправителя также могли измениться.'
            : failure instanceof ApiError && failure.status === 409
              ? kind === 'owner' ? 'Полномочия приглашающего владельца или аккаунт изменились, либо регистрация ещё выполняется. Проверьте результат; при изменении прав попросите новое приглашение с повторным подтверждением.'
                : 'Регистрация уже выполняется либо состав, доступ или аккаунт изменились. Проверьте результат; при необходимости обратитесь к отправителю.'
              : 'Ответ не подтверждён. Повторите действие для проверки результата. Не создавайте другой аккаунт и не передавайте пароль отправителю.'} />}
          {auth.isLoading ? <p role="status">Проверяем вход…</p> : auth.isAuthenticated ? <>
            <p>Вы вошли как {String(auth.user?.profile.email ?? auth.user?.profile.preferred_username ?? 'пользователь')}.</p>
            <label className="invitation-check"><input type="checkbox" checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)} /> {kind === 'owner'
                ? 'Подтверждаю равные права, доступ ко всем данным дистрибьютора и отсутствие отключения совладельца в первой версии.'
                : 'Я прочитал роль и область доступа и подтверждаю принятие.'}</label>
            <Button variant="primary" disabled={!confirmed} loading={pending}
              onClick={() => void accept()}>Подтвердить доступ</Button>
            <p>Не ваш аккаунт? Выйдите и откройте исходную ссылку снова.</p>
            <Button disabled={pending} onClick={() => { void auth.logout().catch(() => setFailure(new Error('Ошибка выхода'))) }}>Выйти из аккаунта</Button>
          </> : <>
            {status && <Banner variant="info" body={status === 'LOGIN_EXISTING_ACCOUNT'
              ? 'Аккаунт с этим email уже существует. Войдите в него; пароль не изменялся.'
              : 'Аккаунт создан. Теперь войдите и подтвердите доступ. Повтор регистрации не заменяет пароль: используйте первоначально заданный.'} />}
            <Button variant="primary" loading={pending} onClick={() => void login()}>Войти и продолжить</Button>
            {!status && invite.state === 'PENDING' && <form onSubmit={(event) => void register(event)} className="invitation-form">
              <h2>Нет аккаунта?</h2>
              <p>Создайте пароль для {invite.email}. Регистрация сама по себе не выдаёт доступ.</p>
              <label htmlFor="invitation-password">Новый пароль<Input id="invitation-password" type="password" autoComplete="new-password" required minLength={8}
                maxLength={128} value={password} disabled={pending}
                onChange={(event) => setPassword(event.target.value)} /></label>
              <label className="invitation-check"><input type="checkbox" required checked={consent}
                disabled={pending} onChange={(event) => setConsent(event.target.checked)} /> Подтверждаю создание моего аккаунта по этому приглашению.</label>
              <Button type="submit" loading={pending}>Создать аккаунт</Button>
            </form>}
          </>}
        </>}
    <p>Не пересылайте эту ссылку посторонним. Сотрудники поддержки не просят сообщить ваш пароль.</p>
  </Card>
}
